import { api } from './authService';
import { PermissionConfig } from '../types/permission';
import { agentChatBIRequestConfig, xProjectIdConfig } from '../utils/projectScope';

// 动态获取当前租户的API基础路径
const getApiBase = (): string => {
  const currentTenant = localStorage.getItem('currentTenant') || 'default';
  return `/${currentTenant}`;
};

export const permissionService = {
  // 角色权限关系配置相关
  async getPermissionConfig(projectId?: string | number | null): Promise<PermissionConfig> {
    const response = await api.get(
      `${getApiBase()}/rolepermissionrelation`,
      xProjectIdConfig(projectId)
    );
    return response.data;
  },

  async reloadConfig(): Promise<void> {
    await api.post(`${getApiBase()}/reload`);
  },

  // 角色管理相关
  async getRoles(): Promise<any[]> {
    const response = await api.get(`${getApiBase()}/roles`);
    return response.data.data || response.data;
  },

  async getRoleById(roleId: string): Promise<any> {
    const response = await api.get(`${getApiBase()}/roles/${roleId}`);
    return response.data.data || response.data;
  },

  async createRole(roleData: {
    name: string;
    displayName: string;
    description?: string;
  }): Promise<void> {
    await api.post(`${getApiBase()}/roles`, roleData);
  },

  async updateRole(roleId: string, roleData: {
    displayName?: string;
    description?: string;
    isActive?: boolean;
  }): Promise<void> {
    await api.put(`${getApiBase()}/roles/${roleId}`, roleData);
  },

  async deleteRole(roleId: string): Promise<void> {
    await api.delete(`${getApiBase()}/roles/${roleId}`);
  },

  // 权限管理相关
  async getPermissions(): Promise<any[]> {
    const response = await api.get(`${getApiBase()}/permissions`);
    return response.data.data || response.data;
  },

  async createPermission(permission: {
    name: string;
    displayName: string;
    description?: string;
    category?: string;
  }): Promise<void> {
    const permissionData = {
      permission_name: permission.name,
      display_name: permission.displayName,
      description: permission.description,
      category: permission.category,
      is_active: true
    };
    await api.post(`${getApiBase()}/permissions`, permissionData);
  },

  async updatePermission(permissionId: string, permissionData: {
    name?: string;
    displayName?: string;
    description?: string;
    category?: string;
    isActive?: boolean;
  }): Promise<void> {
    const updateData: any = {};
    if (permissionData.name !== undefined) updateData.permission_name = permissionData.name;
    // 确保 displayName 被传递，即使是空字符串
    if (permissionData.displayName !== undefined) {
      updateData.display_name = permissionData.displayName || ''; // 空字符串也要传递
    }
    // 确保 description 被传递，即使是空字符串
    if (permissionData.description !== undefined) {
      updateData.description = permissionData.description || ''; // 空字符串也要传递
    }
    if (permissionData.category !== undefined) updateData.category = permissionData.category;
    // 将布尔值转换为数字 1 或 0
    if (permissionData.isActive !== undefined) {
      updateData.is_active = permissionData.isActive ? 1 : 0;
    }
    
    console.log('更新权限请求数据:', updateData);
    await api.put(`${getApiBase()}/permissions/${permissionId}`, updateData);
  },

  async deletePermission(permissionId: string): Promise<void> {
    await api.delete(`${getApiBase()}/permissions/${permissionId}`);
  },

  // 角色权限关系管理
  async getRolePermissions(roleId: string, projectId?: string | number | null): Promise<string[]> {
    const response = await api.get(
      `${getApiBase()}/roles/${roleId}/permissions`,
      xProjectIdConfig(projectId)
    );
    return response.data.data || response.data;
  },

  async updateRolePermissions(role: string, permissions: string[], projectId?: string | number | null): Promise<void> {
    await api.put(
      `${getApiBase()}/roles/${role}/permissions`,
      { permissions },
      xProjectIdConfig(projectId)
    );
  },

  async checkPermissions(sql: string, role: string): Promise<any> {
    const response = await api.post(`${getApiBase()}/check`, { sql, role });
    return response.data;
  },

  // 权限导出相关
  async exportPermissions(): Promise<{
    success: boolean;
    message: string;
    data: {
      filename: string;
      filePath: string;
      downloadUrl: string;
      exportInfo: {
        tenantCode: string;
        exportTime: string;
        totalRoles: number;
        totalPermissions: number;
        activePermissions: number;
      };
    };
  }> {
    const response = await api.post(`${getApiBase()}/export`);
    return response.data;
  },

  async getExportFiles(): Promise<{
    success: boolean;
    data: Array<{
      filename: string;
      downloadUrl: string;
      size: number;
      created: string;
    }>;
  }> {
    const response = await api.get(`${getApiBase()}/export/files`);
    return response.data;
  },

  async downloadPermissionFile(filename: string): Promise<any> {
    const response = await api.get(`${getApiBase()}/export/${filename}`);
    return response.data;
  },

  async publishAgentPermissions(projectId?: string | number | null): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    const response = await api.post(
      `${getApiBase()}/permissions/agent/publish`,
      undefined,
      agentChatBIRequestConfig(projectId)
    );
    return response.data;
  },

  async restoreAgentPermissions(projectId?: string | number | null): Promise<{
    success: boolean;
    message?: string;
    data?: any;
  }> {
    const response = await api.post(
      `${getApiBase()}/permissions/agent/restore`,
      undefined,
      agentChatBIRequestConfig(projectId)
    );
    return response.data;
  },

  async backupRemoteAgentPermissions(projectId?: string | number | null): Promise<{
    success: boolean;
    message?: string;
    data?: {
      filename: string;
      filePath: string;
      downloadUrl: string;
      exportInfo: any;
    };
  }> {
    const response = await api.post(
      `${getApiBase()}/permissions/agent/backup-remote`,
      undefined,
      agentChatBIRequestConfig(projectId)
    );
    return response.data;
  },

  async importPermissions(payload: any): Promise<{
    success: boolean;
    message: string;
    data?: {
      roleCount: number;
      permissionCount: number;
    };
  }> {
    const response = await api.post(`${getApiBase()}/permissions/import`, payload);
    return response.data;
  }
};
